/* eslint-disable max-statements */

import { Box, Button, Grid, Group, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { cloneDeep, get } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TSDViewConsignmentFormValues } from '@e-origin/contracts';

import { BackButton, SearchBox, Table } from '@components';
import { EditGoodsItem } from './EditGoodsItem';
import { useGoodsItemsListColumns } from './useGoodsItemsListColumns';

interface GoodsItemsListProps {
  consignmentForm: UseFormReturnType<TSDViewConsignmentFormValues>;
  disabled: boolean;
}

interface GoodsItemTableData {
  goodsItemNumber: string;
  descriptionOfGoods: string;
  harmonizedSystemSubHeadingCode: string;
  combinedNomenclatureCode: string;
  grossMass: string;
  cusCode: string;
  consignmentItemIdx: number;
}

export const GoodsItemsList: React.FC<GoodsItemsListProps> = ({ consignmentForm, disabled }) => {
  const [tableData, setTableData] = useState([]);

  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const queryHouseRef = queryParams.current.get('houseRef');
  const houseRef = queryHouseRef ? (queryHouseRef === 'master' ? queryHouseRef : Number(queryHouseRef)) : null;
  const isMaster = houseRef === 'master';
  const navigate = useNavigate();

  const searchQuery = useRef<string>(queryParams.current.get('goodsSearch') ? queryParams.current.get('goodsSearch') : '');
  const selectedRow = useRef<GoodsItemTableData | null>(null);

  const consignorName = useMemo(() => {
    const houseConsignment = isMaster ? consignmentForm?.values?.master : consignmentForm?.values?.houseConsignment?.[houseRef];
    return houseConsignment.consignor?.name || '';
  }, [consignmentForm.values, houseRef]);

  const buildTableData = () => {
    const data: GoodsItemTableData[] = [];

    const houseConsigments =
      houseRef !== null
        ? isMaster
          ? consignmentForm.values.master
            ? [consignmentForm.values.master]
            : []
          : [consignmentForm.values.houseConsignment[houseRef]]
        : consignmentForm.values.houseConsignment || [];

    for (const houseItem of houseConsigments) {
      for (const [consignmentItemIdx, consignmentItem] of (houseItem.consignmentItems || []).entries()) {
        if (
          !searchQuery.current ||
          consignmentItem.commodity?.descriptionOfGoods?.toLowerCase().includes(searchQuery.current.toLowerCase()) ||
          consignmentItem.commodity?.commodityCode?.harmonizedSystemSubHeadingCode?.toLowerCase().includes(searchQuery.current.toLowerCase())
        ) {
          data.push({
            goodsItemNumber: consignmentItem.goodsItemNumber,
            descriptionOfGoods: consignmentItem.commodity?.descriptionOfGoods,
            harmonizedSystemSubHeadingCode: consignmentItem.commodity?.commodityCode?.harmonizedSystemSubHeadingCode,
            combinedNomenclatureCode: consignmentItem.commodity?.commodityCode?.combinedNomenclatureCode,
            cusCode: consignmentItem.commodity?.cusCode,
            grossMass: consignmentItem?.weight?.grossMass,
            consignmentItemIdx,
          });
        }
      }
    }

    return data;
  };

  useEffect(() => {
    if (consignmentForm.values && routeLocation) {
      setTableData(buildTableData());
    }
  }, [consignmentForm.values, routeLocation]);

  const handleSave = (goodsItem) => {
    const { consignmentItemIdx = -1 } = selectedRow.current || {};
    const consignmentItemsPath = isMaster ? `master.consignmentItems` : `houseConsignment.${houseRef}.consignmentItems`;

    if (consignmentItemIdx === -1) {
      if (isMaster) {
        const master = cloneDeep(consignmentForm.values.master || {});
        master.consignmentItems = [...(master.consignmentItems || []), goodsItem];
        consignmentForm.setFieldValue('master', master);
      } else {
        consignmentForm.setFieldValue(consignmentItemsPath, [...(get(consignmentForm.values, consignmentItemsPath) || []), goodsItem]);
      }
    } else {
      consignmentForm.setFieldValue(`${consignmentItemsPath}.${consignmentItemIdx}`, goodsItem);
    }
    selectedRow.current = null;
  };

  const handleSearch = (value: string) => {
    searchQuery.current = value;
    setTableData(buildTableData());

    queryParams.current.set('goodsSearch', value);
    navigate({ search: queryParams.current.toString() });
  };

  const openGoodsItem = (row?) => {
    modals.open({
      title: `${!row ? 'Create' : 'Update'} Goods Item`,
      children: (
        <EditGoodsItem
          consignmentForm={consignmentForm}
          onSave={handleSave}
          onHide={modals.closeAll}
          houseConsignmentIdx={houseRef === 'master' ? null : houseRef}
          isMaster={isMaster}
          goodsItemIdx={row?.consignmentItemIdx}
          disabled={disabled}
        />
      ),
      size: '60vw',
    });
  };

  const { columns } = useGoodsItemsListColumns({
    consignmentForm,
    houseRef,
    isMaster,
    openGoodsItem,
    disabled,
    selectedRow,
  });

  return (
    <>
      <BackButton backPath={routeLocation.pathname} data-pw="transit-goods-items-back-button"></BackButton>
      <Grid justify="space-between" align="center">
        <Grid.Col span={4}>
          <Title order={5} fw={600}>
            Goods items {consignorName}
          </Title>
        </Grid.Col>

        <Grid.Col span={6}>
          <Group display={'flex'} py={12} justify="end" gap={10}>
            <SearchBox handleSearch={handleSearch} value={searchQuery.current} placeholder="Search goods by HS code, description of goods" />
            {!disabled && (
              <Button onClick={() => openGoodsItem()} variant="outline" size="lg" data-pw="add-goods-item-btn">
                Add new
              </Button>
            )}
          </Group>
        </Grid.Col>
      </Grid>
      <Box mb={20}>
        <Table key={searchQuery.current} columns={columns} dataSource={tableData || []} pagination verticalSpacing="xs" />
      </Box>
    </>
  );
};
